import { Link, useNavigate } from 'react-router-dom';
import { formatRelativeAge } from '../../lib/format';
import { GENERATION_PATH } from '../generation/generationPrefill';
import { newsItemPrefill, newsSourceName } from '../news/newsPrefill';
import { useNews } from '../news/useNews';
import { PanelLink } from './PanelLink';

/** Headlines shown on Home; the feed itself is one click away. */
const FEED_VISIBLE = 3;

/**
 * The top of the industry feed. It reads what is already pulled (`useNews`
 * never refreshes on load), so opening Home spends nothing. "Generate post" is
 * the news page's own hand-off (`newsItemPrefill`), drawn quiet here because
 * Home's lead action is the quick start.
 */
export function FeedPanel({ businessId }: { businessId: string }) {
  const navigate = useNavigate();
  const news = useNews(businessId);
  const items = news.feed?.items.slice(0, FEED_VISIBLE) ?? [];

  return (
    <section className="home-panel" aria-labelledby="feed-title">
      <div className="home-panel__head">
        <h2 className="home-panel__title" id="feed-title">
          From your feed
        </h2>
        <PanelLink to="/news">Open news</PanelLink>
      </div>

      {news.isLoading ? (
        <p className="kb-section__status" role="status">
          Reading the feed
        </p>
      ) : items.length === 0 ? (
        <p className="home-panel__empty">
          {news.error ?? 'No headlines yet.'} <Link to="/news">Open the feed</Link>
        </p>
      ) : (
        <ul className="feed-list">
          {items.map((item, index) => {
            const prefill = newsItemPrefill(item);
            return (
              <li className="feed-list__item" key={`${index}-${item.url}`}>
                <p className="feed-list__title">{item.title}</p>
                <p className="feed-list__meta">
                  {newsSourceName(item)}
                  {item.published_at ? ` · ${formatRelativeAge(item.published_at)}` : ''}
                </p>
                {prefill ? (
                  <button
                    className="button button--ghost button--small feed-list__action"
                    type="button"
                    onClick={() => void navigate(GENERATION_PATH, { state: prefill })}
                  >
                    Create post
                    <span className="visually-hidden">: {item.title}</span>
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
